import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart3, FileBarChart, Layers, TrendingUp, Bookmark, Plus, Trash2 } from "lucide-react";
import { CHART_PALETTE } from "@/components/charts/ChartKit";

const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const ppw = (n) => (n == null ? "—" : `$${Number(n).toFixed(3)}/W`);

function StatTable({ title, rows, minSample }) {
  if (!rows?.length) return null;
  return (
    <div className="mb-5" data-testid={`bench-${title.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200">
            <th className="py-1.5 pr-2">Slice</th><th className="py-1.5 px-2">n</th><th className="py-1.5 px-2">P25</th>
            <th className="py-1.5 px-2">Median</th><th className="py-1.5 px-2">P75</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-slate-50">
                <td className="py-1.5 pr-2 text-slate-700">{r.key}{r.count < (minSample || 3) ? <span className="text-[10px] text-slate-300 ml-1.5">· thin</span> : null}</td>
                <td className="py-1.5 px-2 font-mono text-slate-500">{r.count}</td>
                <td className="py-1.5 px-2 font-mono text-slate-500">{ppw(r.p25)}</td>
                <td className="py-1.5 px-2 font-mono font-semibold text-[#003E87]">{ppw(r.median)}</td>
                <td className="py-1.5 px-2 font-mono text-slate-500">{ppw(r.p75)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// $/W spread strip — a mini box-plot per slice (P25–P75 band + median marker).
function PpwStrip({ rows, palette }) {
  const clean = (rows || []).filter((r) => r.median != null);
  if (!clean.length) return <div className="text-sm text-slate-400">Not enough sent/won bids to chart yet.</div>;
  const lo = Math.min(...clean.map((r) => r.p25 ?? r.median));
  const hi = Math.max(...clean.map((r) => r.p75 ?? r.median));
  const span = hi - lo || 1;
  const posPct = (v) => ((v - lo) / span) * 100;
  return (
    <div className="space-y-3">
      {clean.map((r, i) => {
        const c = palette[i % palette.length];
        const l = posPct(r.p25 ?? r.median);
        const rr = posPct(r.p75 ?? r.median);
        return (
          <div key={r.key} data-testid={`ppw-strip-${i}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600 font-medium truncate">{r.key} <span className="text-slate-300">· n {r.count}</span></span>
              <span className="font-mono font-semibold" style={{ color: c }}>${Number(r.median).toFixed(3)}/W</span>
            </div>
            <div className="relative h-6 rounded-md bg-slate-100">
              <div className="absolute top-0 bottom-0 rounded-md opacity-25" style={{ left: `${l}%`, right: `${100 - rr}%`, background: c }} />
              <div className="absolute top-0 bottom-0 w-0.5 rounded" style={{ left: `${posPct(r.median)}%`, background: c }} />
            </div>
          </div>
        );
      })}
      <div className="flex justify-between text-[10px] text-slate-400 font-mono"><span>${lo.toFixed(3)}/W</span><span>${hi.toFixed(3)}/W</span></div>
    </div>
  );
}

export default function Reports() {
  const [view, setView] = useState("summary");
  const [bench, setBench] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidId, setBidId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [ballparkMw, setBallparkMw] = useState(100);
  const [form, setForm] = useState({ name: "", from_bid_id: "none", ppw: "", notes: "" });
  useEffect(() => { api.get("/rate-cards").then((r) => setCards(r.data || [])).catch(() => {}); }, []);
  const saveCard = async () => {
    const body = { name: form.name, notes: form.notes };
    if (form.from_bid_id && form.from_bid_id !== "none") body.from_bid_id = form.from_bid_id;
    if (form.ppw !== "") body.ppw = Number(form.ppw);
    try {
      const r = await api.post("/rate-cards", body);
      setCards((c) => [r.data, ...c]); setForm({ name: "", from_bid_id: "none", ppw: "", notes: "" });
      toast.success("Rate card saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save card"); }
  };
  const deleteCard = async (id) => {
    try { await api.delete(`/rate-cards/${id}`); setCards((c) => c.filter((x) => x.card_id !== id)); }
    catch { toast.error("Could not delete card"); }
  };

  useEffect(() => { api.get("/insights/benchmarks").then((r) => setBench(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/bids").then((r) => setBids((r.data || []).slice(0, 300))).catch(() => {}); }, []);
  useEffect(() => {
    if (!bidId) { setReport(null); return; }
    setLoading(true);
    api.get(`/bids/${bidId}/report`).then((r) => setReport(r.data)).catch(() => setReport(null)).finally(() => setLoading(false));
  }, [bidId]);

  const splitTotal = report ? Object.values(report.cost_split || {}).reduce((a, b) => a + (b || 0), 0) : 0;
  const SPLIT_COLORS = { labor: "#003E87", equipment: "#F4C400", material: "#64748B", per_diem: "#0EA5E9" };

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8" data-testid="reports-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2 mb-1">
        <FileBarChart className="w-5 h-5 text-[#003E87]" /> Reports
      </h1>
      <p className="text-sm text-slate-500 mb-5">Estimate summaries with the cost split and price sensitivity, plus price-per-watt benchmarks across the book.</p>

      <div className="flex items-center gap-2 mb-6">
        {[["summary", "Estimate report", Layers], ["benchmarks", "Benchmarks", BarChart3], ["cards", "Rate Cards", Bookmark]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`reports-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === k ? "bg-black text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {view === "summary" && (
        <div>
          <Select value={bidId} onValueChange={setBidId}>
            <SelectTrigger className="w-72 mb-5" data-testid="reports-bid-select"><SelectValue placeholder="Pick a bid to summarize" /></SelectTrigger>
            <SelectContent>{bids.map((b) => <SelectItem key={b.bid_id} value={b.bid_id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          {loading && <p className="text-sm text-slate-400">Computing…</p>}
          {report && (
            <div className="space-y-5" data-testid="reports-estimate-summary">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Direct cost", report.totals?.direct_cost], ["General Conditions", report.totals?.general_conditions],
                  ["Total", report.totals?.total], ["$ / Watt", report.totals?.cost_per_watt != null ? `$${report.totals.cost_per_watt}/W` : "—"]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-slate-100 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
                    <div className="text-base font-semibold text-slate-800 font-mono">{typeof v === "string" ? v : money(v)}</div>
                  </div>
                ))}
              </div>

              <Card className="p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-[#003E87]" /> Cost split</div>
                <div className="flex h-4 rounded overflow-hidden mb-2">
                  {Object.entries(report.cost_split || {}).map(([k, v]) => splitTotal ? (
                    <div key={k} style={{ width: `${(v / splitTotal) * 100}%`, backgroundColor: SPLIT_COLORS[k] }} title={`${k}: ${money(v)}`} />
                  ) : null)}
                </div>
                <div className="flex flex-wrap gap-3 text-[11px]">
                  {Object.entries(report.cost_split || {}).map(([k, v]) => (
                    <span key={k} className="flex items-center gap-1 text-slate-500">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SPLIT_COLORS[k] }} />{k.replace("_", "-")}: <span className="font-mono">{money(v)}</span>
                    </span>
                  ))}
                </div>
              </Card>

              <Card className="p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Scope rollup</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200">
                      <th className="py-1.5 pr-2">Scope</th><th className="py-1.5 px-2">Labor</th><th className="py-1.5 px-2">Equip</th>
                      <th className="py-1.5 px-2">Material</th><th className="py-1.5 px-2">Per-diem</th><th className="py-1.5 px-2 text-right">Subtotal</th></tr></thead>
                    <tbody>
                      {report.rollup.map((r, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`rollup-row-${i}`}>
                          <td className="py-1.5 pr-2 text-slate-700">{r.name}</td>
                          <td className="py-1.5 px-2 font-mono text-slate-500">{money(r.labor)}</td>
                          <td className="py-1.5 px-2 font-mono text-slate-500">{money(r.equipment)}</td>
                          <td className="py-1.5 px-2 font-mono text-slate-500">{money(r.material)}</td>
                          <td className="py-1.5 px-2 font-mono text-slate-500">{money(r.per_diem)}</td>
                          <td className="py-1.5 px-2 font-mono font-semibold text-slate-800 text-right">{money(r.subtotal)}</td>
                        </tr>
                      ))}
                      {report.rollup.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-xs text-slate-400">No priced scopes yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card className="p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Price sensitivity</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] text-slate-400 mb-1">Productivity swing</div>
                    {report.sensitivity.cost.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-0.5" data-testid={`sens-cost-${i}`}>
                        <span className="text-slate-600">{s.label}</span><span className="font-mono text-slate-800">{money(s.total)}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-400 mb-1">Markup swing</div>
                    {report.sensitivity.markup.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-0.5" data-testid={`sens-markup-${i}`}>
                        <span className="text-slate-600">Markup {s.markup_pct}%</span><span className="font-mono text-slate-800">{money(s.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}

      {view === "benchmarks" && bench && (
        <div data-testid="reports-benchmarks">
          <Card className="p-5 mb-5" data-testid="ppw-benchmark-chart">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">$/W spread — P25 · median · P75 by discipline</div>
            <PpwStrip rows={bench.by_discipline} palette={CHART_PALETTE} />
          </Card>
          <Card className="p-4 mb-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Overall $/W (won &amp; sent bids)</div>
            {bench.overall?.count ? (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {[["n", bench.overall.count], ["P25", ppw(bench.overall.p25)], ["Median", ppw(bench.overall.median)], ["P75", ppw(bench.overall.p75)], ["Range", `${ppw(bench.overall.min)}–${ppw(bench.overall.max)}`]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-slate-100 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
                    <div className="text-sm font-semibold text-slate-800 font-mono">{v}</div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">Not enough sent/won bids with a price-per-watt yet.</p>}
          </Card>
          <Card className="p-4">
            <StatTable title="By discipline (scope type)" rows={bench.by_discipline} minSample={bench.min_sample} />
            <StatTable title="By primary discipline" rows={bench.by_primary_discipline} minSample={bench.min_sample} />
            <StatTable title="By scope completeness" rows={bench.by_completeness} minSample={bench.min_sample} />
            <StatTable title="By size band" rows={bench.by_size} minSample={bench.min_sample} />
            <StatTable title="By state" rows={bench.by_state} minSample={bench.min_sample} />
          </Card>
        </div>
      )}

      {view === "cards" && (
        <div data-testid="reports-rate-cards">
          <Card className="p-4 mb-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1.5"><Plus className="w-4 h-4 text-[#003E87]" /> Save a rate card</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <Input placeholder="Card name (e.g. TX EPC 100MW)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="rate-card-name" />
              <Select value={form.from_bid_id} onValueChange={(v) => setForm((f) => ({ ...f, from_bid_id: v }))}>
                <SelectTrigger data-testid="rate-card-from-bid"><SelectValue placeholder="Derive $/W from a bid (optional)" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Manual $/W (no bid)</SelectItem>{bids.map((b) => <SelectItem key={b.bid_id} value={b.bid_id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" step="0.001" placeholder="$/W (if no bid chosen)" value={form.ppw} onChange={(e) => setForm((f) => ({ ...f, ppw: e.target.value }))} data-testid="rate-card-ppw" />
              <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="rate-card-notes" />
            </div>
            <Button onClick={saveCard} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold" data-testid="rate-card-save">Save rate card</Button>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your rate cards — price a new job</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">Ballpark for
                <Input type="number" value={ballparkMw} onChange={(e) => setBallparkMw(Number(e.target.value || 0))} className="h-7 w-20 text-xs font-mono" data-testid="rate-card-mw" /> MW</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200">
                  <th className="py-1.5 pr-2">Card</th><th className="py-1.5 px-2">$/W</th><th className="py-1.5 px-2">Scope · size</th>
                  <th className="py-1.5 px-2 text-right">Ballpark @ {ballparkMw} MW</th><th className="py-1.5 px-2"></th></tr></thead>
                <tbody>
                  {cards.map((c) => (
                    <tr key={c.card_id} className="border-b border-slate-50" data-testid={`rate-card-${c.card_id}`}>
                      <td className="py-1.5 pr-2 text-slate-700">{c.name}{c.source ? <span className="text-[10px] text-slate-400 ml-1">· from {c.source}</span> : null}</td>
                      <td className="py-1.5 px-2 font-mono font-semibold text-[#003E87]">${c.ppw}/W</td>
                      <td className="py-1.5 px-2 text-slate-500 capitalize">{c.discipline} · {c.size_band}</td>
                      <td className="py-1.5 px-2 font-mono font-semibold text-slate-800 text-right">{money(c.ppw * (ballparkMw || 0) * 1_000_000)}</td>
                      <td className="py-1.5 px-2 text-right"><button onClick={() => deleteCard(c.card_id)} className="text-slate-300 hover:text-rose-600" data-testid={`rate-card-delete-${c.card_id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                  {cards.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-xs text-slate-400">No rate cards yet. Save one from a won bid or enter a $/W above.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
